import React from "react";
import List from "@mui/material/List";
import {BASE_URL_MESSENGER_API, URL_CONTACT_AVATAR, URL_GROUP_AVATAR} from "../../Const";
import { MessengerUserRepository } from "../../Repository/messenger/MessengerUserRepository";
import { AudioPlayer } from "./AudioPlayer";
import { TelegramInputBar } from "./TelegramInputBar";
import { VideoPlayer } from "./VideoPlayer";
import { DocumentPlayer } from "./DocumentPlayer";
import { ImageViewer } from "./ImageViewer";
import PersonIcon from '@mui/icons-material/Person';
import { ContactRepository } from "../../Repository/messenger/ContactRepository";
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CallIcon from '@mui/icons-material/Call';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CircularProgress from '@mui/material/CircularProgress';
import { ImageLoader } from "./ImageLoader";
import {GroupRepository} from "../../Repository/messenger/GroupRepository";
import {GroupOrContactProfileModal} from "./GroupOrContactProfileModal";

export class ChatComponent extends React.Component {
    constructor(props) {
        super(props);

        const chatId = this.props.chat_id;
        const contact = ContactRepository.findByChatId(chatId);
        const group = GroupRepository.findByChatId(chatId);

        this.state = {
            chatList: [],
            isFetching: false,
            hasMore: true,
            user: MessengerUserRepository.get(),
            chatId: chatId,
            contact: contact,
            group: group,
            scrollHeightBeforeFetch: 0,
            initialLoadDone: false,
            initialTimestamp: Date.now(),
            profileModalOpen: false
        };

        this.openProfileModal = this.openProfileModal.bind(this);
        this.messagesContainerRef = React.createRef();
        this.messagesEndRef = React.createRef();

    }

    componentDidMount() {
        this.fetchMessages(this.state.initialTimestamp, true).then(() => {
            setTimeout(() => this.scrollToBottom(), 0);
        });

        if (this.messagesContainerRef && this.messagesContainerRef.current) {
            this.messagesContainerRef.current.addEventListener("scroll", this.handleScroll);
        }
    }

    componentWillUnmount() {
        if (this.messagesContainerRef && this.messagesContainerRef.current) {
            this.messagesContainerRef.current.removeEventListener("scroll", this.handleScroll);
        }
    }

    fetchMessages = (timestamp, scrollToBottom = false) => {
        if (this.state.isFetching || (!this.state.hasMore && !scrollToBottom)) return Promise.resolve();

        this.setState({ isFetching: true });

        const container = this.messagesContainerRef.current;
        const oldScrollHeight = container ? container.scrollHeight : 0;

        return fetch(`${BASE_URL_MESSENGER_API}chats?chat_id=${this.state.chatId}&target_timestamp=${timestamp}&per_page=30`, {
            headers: {
                'Authorization': 'Bearer ' + this.state.user.token,
                'Content-Type': 'application/json'
            }
        })
            .then(res => res.json())
            .then(results => {
                if (results.code === "200") {
                    const newMessages = results.data.reverse();
                    return new Promise(resolve => {
                        this.setState(prevState => {
                            const merged = scrollToBottom
                                ? [...newMessages]
                                : [...newMessages, ...prevState.chatList];
                            return {
                                chatList: merged,
                                hasMore: newMessages.length === 30,
                                isFetching: false,
                                initialLoadDone: true
                            };
                        }, () => {
                            if (scrollToBottom) {
                                this.scrollToBottom();
                            } else if (container) {
                                const newScrollHeight = container.scrollHeight;
                                container.scrollTop = newScrollHeight - oldScrollHeight;
                            }
                            resolve();
                        });
                    });
                }
                return Promise.resolve();
            })
            .catch(() => {
                this.setState({ isFetching: false });
                return Promise.resolve();
            });
    };

    handleScroll = () => {
        const container = this.messagesContainerRef.current;
        if (container && container.scrollTop < 30 && this.state.chatList.length > 0) {
            const oldest = this.state.chatList[0];
            this.fetchMessages(oldest.created_at_sender);
        }
    };

    scrollToBottom = () => {
        if (this.messagesEndRef.current) {
            this.messagesEndRef.current.scrollIntoView({ behavior: "auto" });
        }
    };

    renderItem = (message, index) => {
        const content = JSON.parse(message.content);
        const isMine = message.sender_user_id === this.state.user.id;
        const isGroup = !!this.state.group && !this.state.contact;

        const { chatList } = this.state;
        const prevMessage = chatList[index - 1];
        const showAvatar = isGroup && !isMine && (!prevMessage || prevMessage.sender_user_id !== message.sender_user_id);

        return (
            <div key={message.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isMine ? 'flex-start' : 'flex-end', direction: 'rtl', marginBottom: 8, padding: '0 12px' }}>
                {showAvatar && (
                    <div style={{ width: 32, height: 32, borderRadius: '50%', overflow: 'hidden', marginBottom: 4 }}>
                        <ImageLoader url={URL_GROUP_AVATAR + this.state.group.avatar} width={32} height={32} />
                    </div>
                )}
                <div style={{ backgroundColor: isMine ? 'rgb(242, 248, 248)' : '#ffffff', borderRadius: 18, padding: '8px 12px', maxWidth: '75%', fontSize: 14, lineHeight: '22px', textAlign: 'right', fontFamily: 'IRANSans', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                    {content.type === "messageText" && <div>{content.text?.text}</div>}
                    {content.type === "messageAudio" && (<><AudioPlayer url={content.audioStruct.audioFile.remoteId} />{content.caption?.text && (<div style={{ marginTop: 6 }}>{content.caption.text}</div>)}</>)}
                    {content.type === "messageVideo" && <VideoPlayer content={content} />}
                    {content.type === "messageDocument" && <DocumentPlayer url={content.documentStruct.documentFile.remoteId} fileName={"file." + content.documentStruct.documentFile.mimeType} caption={content.caption?.text} />}
                    {content.type === "messageImage" && <ImageViewer url={content.imageStruct.imageFile.remoteId} thumbnail={content.imageStruct.thumbnail} width={content.imageStruct.width} height={content.imageStruct.height} caption={content.caption?.text} />}
                    {content.type === "messageContact" && (
                        <a href={`tel:${content.shareContactStruct.number}`} style={{ textDecoration: 'none', color: 'inherit', marginTop: 6, display: 'block' }}>
                            <div style={{ display: 'flex', alignItems: 'center', padding: '8px', border: '1px solid #ccc', borderRadius: 12, backgroundColor: '#f5f5f5' }}>
                                <div style={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: '#e0e0e0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 12, overflow: 'hidden' }}>
                                    {content.shareContactStruct.avatar ? (
                                        <ImageLoader url={content.shareContactStruct.avatar} width={48} height={48} />
                                    ) : (
                                        <PersonIcon style={{ fontSize: 32, color: '#757575' }} />
                                    )}
                                </div>
                                <div style={{ textAlign: 'right', flex: 1 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: 15 }}>{content.shareContactStruct.name}</div>
                                    <div style={{ fontSize: 13, color: '#666' }}>{content.shareContactStruct.number}</div>
                                    {content.shareContactStruct.description && (<div style={{ fontSize: 13, marginTop: 4 }}>{content.shareContactStruct.description}</div>)}
                                    {content.shareContactStruct.email && (<div style={{ fontSize: 13, marginTop: 2, color: '#007bff' }}>{content.shareContactStruct.email}</div>)}
                                </div>
                            </div>
                        </a>
                    )}
                    <div style={{ fontSize: 11, color: '#999', marginTop: 6, display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                        <span>{message.created_at?.split("T")[1]?.substring(0, 5) || "۱۲:۳۴"}</span>
                        {isMine && <DoneAllIcon style={{ fontSize: 16, color: '#4FC3F7' }} />}
                    </div>
                </div>
            </div>
        );
    };

    openProfileModal() {
        this.setState({ profileModalOpen: true });
    }

    render() {
        const isGroup = !this.state.contact && !!this.state.group;
        const name = isGroup ? this.state.group.title : this.state.contact?.name;
        const avatar = isGroup ? this.state.group.avatar : this.state.contact?.avatar;
        const is_online = isGroup ? false : this.state.contact?.is_online;
        const phone = this.state.contact?.number;

        return (
            <div style={{
                height: '100dvh',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                direction: 'rtl',
                backgroundColor: '#f0f2f5',
                backgroundImage: 'url("/assets/chat_wallpaper.png")',
                backgroundSize: '180px',
                backgroundAttachment: 'fixed',
                fontFamily: 'IRANSans, sans-serif'
            }}>
                <div style={{
                    height: 56,
                    backgroundColor: '#ffffff',
                    borderBottom: '1px solid #eee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 12px',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.1)'
                }}>
                    <div style={{ cursor: 'pointer' }} onClick={() => window.history.back()}><ArrowForwardIosIcon fontSize="small" /></div>
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1, marginRight: 12 }}>
                            <div style={{ width: 42, height: 42, borderRadius: '50%', overflow: 'hidden', marginLeft: 12, border: is_online ? '2px solid #4CAF50' : '2px solid transparent' }}>
                                {avatar ? (
                                    <ImageLoader url={(isGroup ? URL_GROUP_AVATAR : URL_CONTACT_AVATAR) + avatar} width={42} height={42} />
                                ) : (
                                    <PersonIcon style={{ fontSize: 32, color: '#757575' }} />
                                )}
                            </div>
                        <div onClick={this.openProfileModal} style={{ cursor: 'pointer' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'right' }}>
                                <span style={{ fontWeight: 'bold', fontSize: 15 }}>{name}</span>
                                <span style={{ fontSize: 12, color: is_online ? '#4CAF50' : '#999' }}>{is_online ? "آنلاین" : "آفلاین"}</span>
                            </div>
                        </div>


                    </div>
                    {!isGroup && (
                        <div style={{ cursor: 'pointer', color: '#007bff' }} onClick={() => window.open(`tel:${phone}`)}>
                            <CallIcon />
                        </div>
                    )}
                </div>
                <div ref={this.messagesContainerRef} style={{ flex: 1, overflowY: 'auto', paddingTop: 8, paddingBottom: 90 }}>
                    {this.state.isFetching && (
                        <div style={{ textAlign: 'center', marginTop: 10 }}>
                            <CircularProgress size={24} thickness={4} />
                        </div>
                    )}
                    <List style={{ padding: 0 }}>
                        {this.state.chatList.map((msg, index) => this.renderItem(msg, index))}
                        <div ref={this.messagesEndRef} />
                    </List>
                </div>
                <TelegramInputBar
                    onSend={(msg) => console.log("📝 Message:", msg)}
                    onFileSelect={(file) => console.log("📎 File selected:", file)}
                    onImageSelect={(img) => console.log("🖼️ Image selected:", img)}
                    onEmojiSelect={(emoji) => console.log("😊 Emoji:", emoji)}
                    onRecordAudio={(blob) => {
                        const url = URL.createObjectURL(blob);
                        console.log("🎤 Audio recorded:", url);
                    }}
                />

                <GroupOrContactProfileModal
                    open={this.state.profileModalOpen}
                    onClose={() => this.setState({ profileModalOpen: false })}
                    data={this.state.group || this.state.contact}
                    currentUserId={this.state.user.id}
                    onEditProfile={() => alert("📝 Edit profile")}
                    onCall={(phone) => window.open(`tel:${phone}`)}
                    onPromote={(userId) => alert("⬆ Promote user " + userId)}
                    onDeleteUser={(userId) => alert("🗑 Delete user " + userId)}
                />
            </div>
        );
    }
}
